import { publishEvent } from 'serviceWorker/broadcasts/publishers';
import { swSignals } from 'serviceWorker/events/swSignals';
import type { ChannelContract, ChannelContractShape } from 'shared/channels';
import Dexie, { ObservabilitySet } from 'dexie';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { issueContract } from 'shared/domain/issue/issueContract';
import { levelContract } from 'shared/domain/level/levelContract';
import { siteContract } from 'shared/domain/site/siteContract';
import { fieldValueContracts } from 'shared/domain/fieldValue/fieldValueContracts';
import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import { companyContract } from 'shared/domain/company/companyContract';
import { contractChannelContract } from 'shared/domain/contract/contractChannelContract';
import { issueFormContract } from 'shared/domain/issueForm/issueFormContract';
import { inspectionContract } from 'shared/domain/inspection/inspectionContract';
import { inspectionTemplateContract } from 'shared/domain/inspectionTemplate/inspectionTemplateContract';
import { visibleFieldContract } from 'shared/domain/visibleField/visibleFieldContract';

/** A contract whose listeners reload when its table changes. */
type TableObservingContract = ChannelContract<
  ChannelContractShape & { events: { [DomainMessagesTypes.tableChanged]: unknown } }
>;

const tablesToObserve: Record<string, TableObservingContract> = {
  'idb://hustro/issues/': issueContract,
  'idb://hustro/levels/': levelContract,
  'idb://hustro/sites/': siteContract,
  'idb://hustro/worktypes/': fieldValueContracts[EditableFieldValuesEntityName.workTypes],
  'idb://hustro/delayReasons/': fieldValueContracts[EditableFieldValuesEntityName.delayReason],
  'idb://hustro/rejectionReasons/': fieldValueContracts[EditableFieldValuesEntityName.rejectionReason],
  'idb://hustro/issueVerificationItems/':
    fieldValueContracts[EditableFieldValuesEntityName.issueVerificationItem],
  'idb://hustro/hazardCategories/': fieldValueContracts[EditableFieldValuesEntityName.hazardCategory],
  'idb://hustro/environmentalAspects/':
    fieldValueContracts[EditableFieldValuesEntityName.environmentalAspect],
  'idb://hustro/correctiveActionTypes/':
    fieldValueContracts[EditableFieldValuesEntityName.proposedCorrectiveAction],
  'idb://hustro/rootCauses/': fieldValueContracts[EditableFieldValuesEntityName.rootCauses],
  'idb://hustro/effects/': fieldValueContracts[EditableFieldValuesEntityName.effect],
  'idb://hustro/circumstances/': fieldValueContracts[EditableFieldValuesEntityName.circumstances],
  'idb://hustro/impacts/': fieldValueContracts[EditableFieldValuesEntityName.impact],
  'idb://hustro/priorities/': fieldValueContracts[EditableFieldValuesEntityName.priority],
  'idb://hustro/projectStages/': fieldValueContracts[EditableFieldValuesEntityName.projectStage],
  'idb://hustro/employmentTypes/': fieldValueContracts[EditableFieldValuesEntityName.employmentType],
  'idb://hustro/injuryTypes/': fieldValueContracts[EditableFieldValuesEntityName.injuryType],
  'idb://hustro/injuredBodyParts/': fieldValueContracts[EditableFieldValuesEntityName.injuredBodyPart],
  'idb://hustro/requirements/': fieldValueContracts[EditableFieldValuesEntityName.requirements],
  'idb://hustro/installationBasis/': fieldValueContracts[EditableFieldValuesEntityName.installationBasis],
  'idb://hustro/constructionProductDocuments/': fieldValueContracts[EditableFieldValuesEntityName.constructionProductDocuments],
  'idb://hustro/deviceDocuments/': fieldValueContracts[EditableFieldValuesEntityName.deviceDocuments],
  'idb://hustro/additionalDocuments/': fieldValueContracts[EditableFieldValuesEntityName.additionalDocuments],
  'idb://hustro/decisionsToImposeFine/':
    fieldValueContracts[EditableFieldValuesEntityName.decisionToImposeFine],
  'idb://hustro/spilledSubstances/': fieldValueContracts[EditableFieldValuesEntityName.spilledSubstance],
  'idb://hustro/companies/': companyContract,
  'idb://hustro/contracts/': contractChannelContract,
  'idb://hustro/issueForm/': issueFormContract,
  'idb://hustro/inspections/': inspectionContract,
  'idb://hustro/inspectionTemplates/':
    inspectionTemplateContract,
  'idb://hustro/visibleFields/': visibleFieldContract,
};

class DbChangeListener {
  constructor() {
    this.stopListeningOnLogout();
  }

  /** Tells the entity channels when their tables change, from now on. */
  startListening(): void {
    this.unsubscribeAll();
    Dexie.on('storagemutated', onStorageMutated);
  }

  /** A project switch needs nothing: the page starts listening again for the new project. */
  private stopListeningOnLogout(): void {
    swSignals.loggedOut.subscribe(() => {
      this.unsubscribeAll();
    });
  }

  private unsubscribeAll(): void {
    Dexie.on('storagemutated').unsubscribe(onStorageMutated);
  }
}

function onStorageMutated(parts: ObservabilitySet): void {
  const keys = Object.keys(parts);
  const table = keys.find((key) => tablesToObserve[key]);
  if (!table) return;
  publishEvent(tablesToObserve[table], DomainMessagesTypes.tableChanged, undefined);
}

let dbChangeListener: DbChangeListener | null = null;

/** Starts telling the entity channels about table changes (the page shows project data). */
export function startListeningToDbChanges(): void {
  dbChangeListener?.startListening();
}

export const init = (): void => {
  dbChangeListener = new DbChangeListener();
};
